import type { AgrianTypes, Branch, Company } from "../../../../models";
import { Oops } from "../../../../utilities/oops";

/**
 * Gets the Organization IDs to use from selected branches and/or company.
 * Priority: Branch IDs -> Company IDs -> Empty array
 *
 * When multiple branches are selected, combines Org IDs from all branches.
 */
export function getOrgIdsFromBranchesAndCompany(selectedBranches: Branch[], selectedCompany: Company | null): string[] {
	try {
		// Collect Org IDs from all selected branches
		const allBranchOrgIds: string[] = [];

		for (const branch of selectedBranches) {
			const branchOrgIds = branch?.externalLocationId;
			if (Array.isArray(branchOrgIds) && branchOrgIds.length > 0) {
				// Add unique org IDs only
				for (const orgId of branchOrgIds) {
					if (!allBranchOrgIds.includes(orgId)) {
						allBranchOrgIds.push(orgId);
					}
				}
			}
		}

		// If any selected branches have org IDs, use them
		if (allBranchOrgIds.length > 0) {
			return allBranchOrgIds;
		}

		// Fall back to company
		const companyOrgIds = selectedCompany?.externalLocationId;
		if (Array.isArray(companyOrgIds) && companyOrgIds.length > 0) {
			return companyOrgIds;
		}

		// No org IDs available
		return [];
	} catch (error) {
		Oops.handleError(error, "SubmitJobs.AgrianImportForm.helpers.getOrgIdsFromBranchesAndCompany");
		return [];
	}
}

/**
 * Checks if an Agrian target event is associated with a specific field.
 * Handles both string and array association formats.
 */
export function isEventAssociatedWithField(event: AgrianTypes.HydratedTargetEvent, field: AgrianTypes.Field): boolean {
	const associationFieldId = event.associations?.["agrian.field"];
	return (
		!!event.associations &&
		!!associationFieldId &&
		((typeof associationFieldId === "string" && associationFieldId === field.id) ||
			(Array.isArray(associationFieldId) && associationFieldId.includes(field.id)))
	);
}

/**
 * Generates a display name for GeoJSON file options.
 * Format: "MMM DD, YYYY - FieldName - type (index)"
 */
export function optionName(
	date: string,
	field: AgrianTypes.Field,
	type: "boundary" | "centroids" | "cores" | "zones",
	index?: number,
): string {
	const dt = new Date(date);
	const dateString = dt.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
	const suffix = index !== undefined ? ` (${index + 1})` : "";
	return `${dateString} - ${field.name} - ${type}${suffix}`;
}
